import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { ProductCard } from '../../components/base/ProductCard';
import { products } from '../../mocks/products';
import { useCart } from '../../context/CartContext';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleFavorite, favorites } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'description' | 'specs' | 'delivery' | 'return'>('description');
  const [selectedImage, setSelectedImage] = useState(0);

  const product = products.find(p => p.id === id);

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 font-poppins">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Ürün Bulunamadı</h2>
          <Link to="/kategori" className="text-gold-600 hover:text-gold-700 cursor-pointer">
            Alışverişe Devam Et
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const isFavorite = favorites.includes(product.id);
  const relatedProducts = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const images = [product.image, product.image, product.image];

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    navigate('/sepet');
  };

  const handleBuyNow = () => {
    handleAddToCart();
  };

  return (
    <div className="min-h-screen bg-gray-50 font-poppins">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <nav className="text-sm text-gray-600 mb-6">
          <Link to="/" className="hover:text-gold-600 cursor-pointer">
            Anasayfa
          </Link>
          <span className="mx-2">/</span>
          <Link to="/kategori" className="hover:text-gold-600 cursor-pointer">
            Ürünler
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-800">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div>
            <div className="bg-white rounded-lg overflow-hidden shadow-sm mb-4 w-full h-[500px]">
              <img
                src={images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`w-full h-24 bg-white rounded-lg overflow-hidden border-2 transition-colors cursor-pointer ${
                    selectedImage === idx ? 'border-gold-600' : 'border-gray-200 hover:border-gold-300'
                  }`}
                >
                  <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover object-top" />
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 lg:p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">{product.name}</h1>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2 text-sm">
                <i className="ri-scales-3-line text-gold-600"></i>
                <span className="text-gray-700"><strong>{product.gram}</strong> gram</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <i className="ri-vip-crown-line text-gold-600"></i>
                <span className="text-gray-700"><strong>{product.karat}</strong> Ayar</span>
              </div>
              <div className={`flex items-center gap-2 text-sm ${product.stock > 10 ? 'text-green-600' : 'text-orange-600'}`}>
                <i className="ri-checkbox-circle-line"></i>
                <span>{product.stock > 10 ? 'Stokta' : `Son ${product.stock} adet`}</span>
              </div>
            </div>

            <div className="border-t border-b border-gray-200 py-6 mb-6">
              <div className="text-4xl font-bold text-gold-600 mb-2">
                {product.price.toLocaleString('tr-TR')} ₺
              </div>
              <p className="text-sm text-gray-600">
                <i className="ri-information-line mr-1"></i>
                Altın fiyatları ödeme sırasında güncellenebilir
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Adet</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-subtract-line"></i>
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock, parseInt(e.target.value) || 1)))}
                  className="w-20 h-10 text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                  min="1"
                  max={product.stock}
                />
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                </button>
              </div>
            </div>

            <div className="flex gap-3 mb-6">
              <button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className="flex-1 bg-gold-600 text-white py-4 rounded-lg font-semibold hover:bg-gold-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shopping-cart-line mr-2"></i>
                Sepete Ekle
              </button>
              <button
                onClick={() => toggleFavorite(product.id)}
                className="w-14 h-14 flex items-center justify-center border-2 border-gold-600 rounded-lg hover:bg-gold-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={`${isFavorite ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gold-600'} text-xl`}></i>
              </button>
            </div>

            <button
              onClick={handleBuyNow}
              disabled={product.stock === 0}
              className="w-full bg-gray-800 text-white py-4 rounded-lg font-semibold hover:bg-gray-900 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              Hemen Al
            </button>

            <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-gray-200">
              <div className="text-center">
                <i className="ri-shield-check-line text-2xl text-gold-600 mb-2"></i>
                <p className="text-xs text-gray-600">Güvenli Ödeme</p>
              </div>
              <div className="text-center">
                <i className="ri-truck-line text-2xl text-gold-600 mb-2"></i>
                <p className="text-xs text-gray-600">Sigortalı Kargo</p>
              </div>
              <div className="text-center">
                <i className="ri-arrow-go-back-line text-2xl text-gold-600 mb-2"></i>
                <p className="text-xs text-gray-600">Kolay İade</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-12">
          <div className="flex gap-6 border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('description')}
              className={`pb-3 px-2 font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'description'
                  ? 'text-gold-600 border-b-2 border-gold-600'
                  : 'text-gray-600 hover:text-gold-600'
              }`}
            >
              Açıklama
            </button>
            <button
              onClick={() => setActiveTab('specs')}
              className={`pb-3 px-2 font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'specs'
                  ? 'text-gold-600 border-b-2 border-gold-600'
                  : 'text-gray-600 hover:text-gold-600'
              }`}
            >
              Teknik Özellikler
            </button>
            <button
              onClick={() => setActiveTab('delivery')}
              className={`pb-3 px-2 font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'delivery'
                  ? 'text-gold-600 border-b-2 border-gold-600'
                  : 'text-gray-600 hover:text-gold-600'
              }`}
            >
              Teslimat
            </button>
            <button
              onClick={() => setActiveTab('return')}
              className={`pb-3 px-2 font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'return'
                  ? 'text-gold-600 border-b-2 border-gold-600'
                  : 'text-gray-600 hover:text-gold-600'
              }`}
            >
              İade Koşulları
            </button>
          </div>

          <div className="text-gray-700">
            {activeTab === 'description' && (
              <div>
                <p className="mb-4">{product.description}</p>
                <p>
                  Emin Kuyumculuk güvencesiyle sunulan bu ürün, resmi sertifikalı ve garantilidir.
                  Tüm ürünlerimiz titizlikle seçilmiş ve kalite kontrolünden geçmiştir.
                </p>
              </div>
            )}
            {activeTab === 'specs' && (
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium">Ağırlık:</span>
                  <span>{product.gram} gram</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium">Ayar:</span>
                  <span>{product.karat} Ayar</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium">Kategori:</span>
                  <span>{product.category}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium">Sertifika:</span>
                  <span>Resmi Sertifikalı</span>
                </div>
              </div>
            )}
            {activeTab === 'delivery' && (
              <div>
                <p className="mb-4">
                  <strong>Ücretsiz Sigortalı Kargo:</strong> Tüm siparişlerinizde ücretsiz ve sigortalı teslimat hizmeti sunuyoruz.
                </p>
                <p className="mb-4">
                  <strong>Teslimat Süresi:</strong> Siparişiniz 1-3 iş günü içinde kargoya teslim edilir.
                </p>
                <p>
                  <strong>Mağazadan Teslim:</strong> İsterseniz siparişinizi mağazamızdan ücretsiz olarak teslim alabilirsiniz.
                </p>
              </div>
            )}
            {activeTab === 'return' && (
              <div>
                <p className="mb-4">
                  Ürünü teslim aldıktan sonra 14 gün içinde iade edebilirsiniz.
                </p>
                <p className="mb-4">
                  İade edilecek ürünün kullanılmamış ve orijinal ambalajında olması gerekmektedir.
                </p>
                <p>
                  İade işlemleri için müşteri hizmetlerimizle iletişime geçebilirsiniz.
                </p>
              </div>
            )}
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Benzer Ürünler</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
